import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { FilterEvent } from 'components/shared/Filters';
import { TableFilter } from '@trnsact/trnsact-shared-types';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { OpportunitiesListTabsType } from 'modules/vendorOpportunity/list/types';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { useOpportunitiesListFilters } from 'modules/vendorOpportunity/list/hooks/useOpportunitiesListFilters';
import { useOpportunitiesListColumns } from 'modules/vendorOpportunity/list/hooks/useOpportunitiesListColumns';
import {
  convertFiltersToTabs,
  getDealPipelineTab,
  getDefaultTabValue,
  getInitialFilterConfig,
  getSetOfDataFilterOptions,
  getStandardTabs,
} from 'modules/vendorOpportunity/list/lib';

import { TabsWrapper } from './TabsWrapper';
import { OpportunitiesListFallback } from './OpportunitiesListFallback';
import { OpportunitiesTableFilters } from './OpportunitiesTableFilters';
import { calcFiltersAmount, VOFilters, VOFilterType } from '../../../../filters';

export const OpportunitiesList = () => {
  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);

  const allowDealPipeline = useSelector(portalConfigurationsSelectors.allowDealPipeline);

  const [search, setSearch] = useState<string>('');
  const [isFiltersOpen, setIsFilterOpen] = useState(false);
  const [currentTabValue, setCurrentTabValue] = useState<OpportunitiesListTabsType>();

  const defaultTabValue = getDefaultTabValue(userProfile);

  const onFilterDelete = (tf: TableFilter) => {
    if (currentTabValue === tf.tableFilterId) {
      handleTabChange(defaultTabValue);
    }
  };

  const handleTabChange = (tab: OpportunitiesListTabsType) => {
    const tabToSet = tabs.find(t => t.value === tab)!.value;
    setCurrentTabValue(tabToSet);
    applyInitialFilterConfig(tab);
  };

  const onTableFiltersChanged = (filters: Partial<VOFilters>, event: FilterEvent) => {
    if (event.type === 'apply') {
      setCurrentFilterConfig(filters);
      handleColumnsVisibilityChange(filters[VOFilterType.SET_OF_DATA]!);
    }

    if (event.type === 'reset') {
      applyInitialFilterConfig(currentTabValue!);
    }
  };

  const applyInitialFilterConfig = (tab: OpportunitiesListTabsType) => {
    const currentTableFilter = tableFilters.find(f => f.tableFilterId === tab);

    const visibleColumnsFilterOptions = currentTableFilter?.config
      ? currentTableFilter.config[VOFilterType.SET_OF_DATA]
      : getSetOfDataFilterOptions(initialVisibleColumns);

    const filterConfig =
      currentTableFilter?.config ?? getInitialFilterConfig(tab, userProfile, visibleColumnsFilterOptions);

    setCurrentFilterConfig(filterConfig);
    handleColumnsVisibilityChange(visibleColumnsFilterOptions);
  };

  const {
    defaultFilter,
    tableFilters,
    currentFilterConfig,
    handleFilterDelete,
    handleSetDefaultFilter,
    setCurrentFilterConfig,
    isFilterDeleting,
    isFiltersDataLoading,
    isSetDefaultTableFilterLoading,
  } = useOpportunitiesListFilters(onFilterDelete);

  const { allColumns, visibleColumns, initialVisibleColumns, handleColumnsVisibilityChange, isLoading } =
    useOpportunitiesListColumns();

  useEffect(() => {
    if (isFiltersDataLoading || isLoading) return;

    handleTabChange(defaultFilter?.tableFilterId ?? defaultTabValue);
  }, [isFiltersDataLoading, isLoading]);

  const filterTabs = convertFiltersToTabs(
    tableFilters,
    handleFilterDelete,
    visibleColumns,
    search,
    setSearch,
    currentFilterConfig
  );

  const standardTabs = getStandardTabs(visibleColumns, userProfile, search, setSearch, currentFilterConfig);
  const dealPipelineTab = getDealPipelineTab(allowDealPipeline, userProfile, account.id, currentFilterConfig);
  const tabs = [...filterTabs, ...standardTabs, ...[dealPipelineTab]];

  return (
    <Box sx={{ height: 'calc(100% - 3rem)' }}>
      <RenderWithCondition condition={!!currentTabValue && !isFilterDeleting} fallBack={<OpportunitiesListFallback />}>
        <TabsContainer<OpportunitiesListTabsType>
          tabs={tabs}
          initialTab={currentTabValue}
          onTabChange={handleTabChange}
          tabsWrapperProps={{ mb: 1 }}
          TabsLabelWrapper={({ children }) => (
            <TabsWrapper
              defaultFilter={defaultFilter}
              currentTabValue={currentTabValue!}
              onFiltersClick={() => setIsFilterOpen(true)}
              filtersAmount={calcFiltersAmount(currentFilterConfig)}
              isSetDefaultTableFilterLoading={isSetDefaultTableFilterLoading}
              onSetAsDefaultClick={() => handleSetDefaultFilter(currentTabValue!)}
            >
              {children}
            </TabsWrapper>
          )}
        />

        <OpportunitiesTableFilters
          allColumns={allColumns}
          tableFilters={tableFilters}
          isFiltersOpen={isFiltersOpen}
          currentTabValue={currentTabValue!}
          setIsFilterOpen={setIsFilterOpen}
          currentFilterConfig={currentFilterConfig}
          onTableFiltersChanged={onTableFiltersChanged}
        />
      </RenderWithCondition>
    </Box>
  );
};
